import { parseUtcDate } from "@/lib/chart/timeframes"

/** Percent change from a reference price (usually the previous close); null when undefined. */
export function percentChange(price: number, reference: number): number | null {
  if (!Number.isFinite(price) || !Number.isFinite(reference) || reference === 0) return null
  return ((price - reference) / reference) * 100
}

/** Prices keep enough decimals for sub-dollar contracts (e.g. 0.8125) and fewer for large ones. */
export function formatPrice(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000) return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
  if (abs >= 10) return value.toFixed(2)
  return value.toFixed(4)
}

// [applies from, divisor, suffix]. Thousands only abbreviate from 10k so 4,512 stays exact.
const ABBREVIATIONS: Array<[number, number, string]> = [
  [1e12, 1e12, "T"],
  [1e9, 1e9, "B"],
  [1e6, 1e6, "M"],
  [1e4, 1e3, "k"],
]

/**
 * Compact number for axes, tooltips and headers: 12.5k, 3.4M, 1.2B. `step` (the axis tick
 * spacing) fixes the decimals so neighbouring ticks read consistently (2.25 / 2.50 / 2.75).
 */
export function formatNumber(
  value: number,
  options: { step?: number; maxDecimals?: number; compact?: boolean } = {},
): string {
  if (!Number.isFinite(value)) return "—"
  const abs = Math.abs(value)
  for (const [threshold, divisor, suffix] of options.compact === false ? [] : ABBREVIATIONS) {
    if (abs >= threshold) {
      const scaled = value / divisor
      const scaledStep = options.step === undefined ? undefined : options.step / divisor
      return `${scaled.toFixed(Math.min(2, decimalsFor(Math.abs(scaled), scaledStep)))}${suffix}`
    }
  }
  const decimals = Math.min(options.maxDecimals ?? 4, decimalsFor(abs, options.step))
  return value.toLocaleString(undefined, { minimumFractionDigits: options.step === undefined ? 0 : decimals, maximumFractionDigits: decimals })
}

function decimalsFor(abs: number, step: number | undefined): number {
  if (step !== undefined && step > 0) {
    // Enough decimals to show the step exactly (0.25 -> 2, 5 -> 0).
    let decimals = 0
    while (decimals < 6 && Math.abs(step * 10 ** decimals - Math.round(step * 10 ** decimals)) > 1e-6) decimals += 1
    return decimals
  }
  if (abs >= 100) return 0
  if (abs >= 10) return 1
  return 2
}

/** "Oct 3, 2026" (or "Oct 2026" for month data) for calendar keys; raw key otherwise (e.g. "Day 07"). */
export function formatTimeLabel(time: string, format: "day" | "month" = "day"): string {
  const date = parseUtcDate(time)
  if (!date) return time
  if (format === "month") return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" })
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
}

/** Appends a unit: "45.2%" for percent, "1,200 Bcf" otherwise. */
export function withUnit(formatted: string, unit: string | null | undefined): string {
  if (!unit) return formatted
  return unit === "%" ? `${formatted}%` : `${formatted} ${unit}`
}

export interface TimeTick {
  index: number
  label: string
}

type Granularity = "day" | "month" | "year"

/**
 * Picks time-axis ticks that fit the visible span: days for about three months, months up to
 * three years, then years. Ticks sit on period boundaries (the first point of each new
 * day/month/year), labelling the year at January boundaries, and are thinned to `maxTicks`.
 */
export function timeTicks(times: string[], maxTicks: number): TimeTick[] {
  if (times.length === 0) return []
  const limit = Math.max(2, maxTicks)
  const dates = times.map(parseUtcDate)
  if (dates.some((date) => date === null)) return evenTicks(times, limit)

  const first = dates[0]!.getTime()
  const last = dates[dates.length - 1]!.getTime()
  const spanDays = (last - first) / 86_400_000
  const granularity: Granularity = spanDays <= 92 ? "day" : spanDays <= 3 * 366 ? "month" : "year"

  const boundaries: TimeTick[] = []
  let previousKey: string | null = null
  dates.forEach((date, index) => {
    const key = periodKey(date!, granularity)
    if (key !== previousKey) {
      if (previousKey !== null || granularity === "day") {
        boundaries.push({ index, label: periodLabel(date!, granularity) })
      }
      previousKey = key
    }
  })
  if (boundaries.length === 0) return evenTicks(times, limit).map((tick) => ({ ...tick, label: formatTimeLabel(times[tick.index]) }))

  const stride = Math.ceil(boundaries.length / limit)
  if (stride === 1) return boundaries
  // Thin on the cadence that keeps year boundaries (January), so the axis still shows years.
  const yearTick = granularity === "month" ? boundaries.findIndex((tick) => /^\d{4}$/.test(tick.label)) : -1
  const offset = yearTick >= 0 ? yearTick % stride : 0
  const thinned = boundaries.filter((_tick, position) => position % stride === offset)
  if (granularity === "month" && !thinned.some((tick) => /^\d{4}$/.test(tick.label)) && thinned.length > 0) {
    const first = dates[thinned[0].index]!
    thinned[0] = { ...thinned[0], label: `${thinned[0].label} ${first.getUTCFullYear()}` }
  }
  return thinned
}

function periodKey(date: Date, granularity: Granularity): string {
  if (granularity === "year") return `${date.getUTCFullYear()}`
  if (granularity === "month") return `${date.getUTCFullYear()}-${date.getUTCMonth()}`
  return date.toISOString().slice(0, 10)
}

function periodLabel(date: Date, granularity: Granularity): string {
  if (granularity === "year") return `${date.getUTCFullYear()}`
  if (granularity === "month") {
    return date.getUTCMonth() === 0
      ? `${date.getUTCFullYear()}`
      : date.toLocaleDateString(undefined, { month: "short", timeZone: "UTC" })
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })
}

function evenTicks(times: string[], limit: number): TimeTick[] {
  const count = Math.min(limit, times.length)
  const ticks: TimeTick[] = []
  for (let tick = 0; tick < count; tick += 1) {
    const index = Math.round((tick / Math.max(1, count - 1)) * (times.length - 1))
    if (ticks.at(-1)?.index !== index) ticks.push({ index, label: times[index] })
  }
  return ticks
}
